/* eslint-disable prettier/prettier */
import React, { createContext, PropsWithChildren, useContext, useEffect, useState } from 'react'
import * as R from 'ramda'
import { PaymentOrder } from '../model-payments'
import {
  Address,
  CartCharge,
  NewAddress,
  Order,
  OrderCtx,
  OrderItem,
  OrderItemModifier,
  OrderItemModifierOption,
  ServiceType,
} from '../model-order'
import { useQuery } from '@apollo/react-hooks'
import { RestaurantsAPI5ClientCtxx } from 'context'
import { GET_RESTAURANT, GET_RESTAURANT_NOWF, GET_RESTAURANT_WWF, Restaurant, RestaurantCtx } from '../model-restaurant'
import { Channel, GET_CHANNEL } from '../model-channel'

import oauthClient from 'api/auth'

import userSettingsAPI from 'stores/user'
import { UserConfigCtx } from '../comp-factory'
import ApolloClient from 'apollo-client'
import { GET_RESTAURANT_MENU, Menus } from '../model-menus'
import * as utils from '../../components/utils'

declare let ORDERS_SVC_API5: string

export interface EditOrderData {
  order: Order
  paymentOrder: PaymentOrder
  actions: EditOrderAction[]
}

export type EditOrderAction =
  | { op: 'add-item'; item: EditOrderItem }
  | { op: 'add-ingredient'; item_id: number; item_name: string; ingredient: EditOrderModifier }
  | { op: 'remove-item'; item_id: number; item_name: string }
  | { op: 'update-quantity'; item_id: number; item_name: string; quantity: number; prev_quantity: number }
  | {
      op: 'remove-customization'
      item_id: number
      item_name: string
      modifier_name: string
      customization_name: string
      customization_id: number
    }
  | { op: 'add-charge'; charge: EditOrderCharge }
  | { op: 'remove-charge'; charge_id: number; charge_name: string }
  | { op: 'update-charge'; charge_id: number; charge: Partial<CartCharge>; value: number; prev_value: number }
  | { op: 'update-delivery'; value: number; prev_value: number }
  | { op: 'update-tip'; value: number; prev_value: number }
  | { op: 'update-service'; value: number; prev_value: number }
  | { op: 'update-order_type'; value: ServiceType; label: string; prev_value: ServiceType; prev_label: string }
  | { op: 'update-delivery_address'; address: NewAddress }
  | { op: 'update-order_time'; value: string; label: string; prev_value: string; prev_label: string }
  | { op: 'update-discount'; value: number; prev_value: number }

export type EditOrderActionType =
  | 'add-item'
  | 'add-modifiers'
  | 'remove-item'
  | 'update-quantity'
  | 'remove-modifiers'
  | 'add-charge'
  | 'remove-charge'
  | 'update-charge'
  | 'update-delivery'
  | 'update-tip'
  | 'update-order_type'
  | 'update-order_time'
  | 'update-delivery_address'

export interface EditOrderItem {
  item_id?: number
  item_name: string
  menu_id?: number
  menu_name: string
  category_id?: number
  category_name: string
  price: number
  quantity: number
  total: number
}

export interface EditOrderModifier {
  id: number
  name: string
  price: number
  quantity: number
  modifier_id: number
  modifier_name: string
}

export interface EditOrderCharge {
  name: string
  amount: number
}

export interface ServiceInfo {
  id: ServiceType
  label: string
}

export interface EditOrderAPI {
  undo: () => EditOrderAction | null
  redo: () => EditOrderAction | null
  hasUndo: () => boolean
  hasRedo: () => boolean
  getChanges: () => EditOrderAction[]
  getChangesCount: () => number
  getDeliveryFee: () => number
  getTip: () => number
  getService: () => number
  getRestaurantId: () => number
  getItem: (cartItemId: number) => OrderItem
  getLastItemVersion(currentItem: OrderItem): OrderItem
  recordChange: (action: EditOrderAction) => void
  discardAction: (action: EditOrderAction) => void
  getMenus: () => Menus
  getServiceCharge: (chargeId: number) => CartCharge
  getOrderUUID: () => string
  getCartCharges: () => CartCharge[]
  getCartItems: () => OrderItem[]
  commitToServer: (paymentId: number, reason: string) => Promise<unknown>
  getLocale: () => string
  getOrderType: () => ServiceInfo
  getOrderTime: () => string
  getAvailableServices: () => ServiceInfo[]
  getDiscount: () => number
  getOrderAddress: () => Address
  // addAction: (action: EditOrderAction) => void
  // getOrder: () => Order
}

const EditOrderCtx = createContext<EditOrderAPI | null>(null)

export function useEditOrderSvc(): EditOrderAPI {
  const api = useContext(EditOrderCtx)
  return api as EditOrderAPI
}

let _nextItemId = -1
function nextItemId(): number {
  return _nextItemId--
}

function reduceActions(actions: EditOrderAction[]): EditOrderAction[] {
  return actions
}

function updateOrder(order: Order, action: EditOrderAction): Order {
  switch (action.op) {
    case 'add-item':
      order = R.over(
        R.lensPath(['carts', 'items']),
        (items) => [
          ...items,
          {
            id: nextItemId(),
            item_id: action.item.item_id,
            category: action.item.category_name,
            size_name: '',
            comments: '',
            dish_name: action.item.item_name,
            item_total: Math.round(action.item.price * 100) * action.item.quantity,
            menu: action.item.menu_name,
            modifiers: [],
            name: action.item.item_name,
            price: Math.round(action.item.price * 100),
            quantity: action.item.quantity,
            action: action,
          },
        ],
        order
      )
      break
    case 'add-ingredient': {
      const itemIndex = R.findIndex((i) => i.id == action.item_id, order.carts.items)
      const item = order.carts.items[itemIndex]
      const modifierIndex = item.modifiers.findIndex((m) => m.name == action.ingredient.modifier_name)
      if (modifierIndex != -1) {
        order = R.over(
          R.lensPath(['carts', 'items', itemIndex, 'modifiers', modifierIndex, 'options']),
          (options) => [
            ...options,
            {
              id: nextItemId(),
              name: action.ingredient.name,
              quantity: action.ingredient.quantity,
              price: Math.round(action.ingredient.price * 100),
              action: action,
            },
          ],
          order
        )
      } else {
        order = R.over(
          R.lensPath(['carts', 'items', itemIndex, 'modifiers']),
          (modifiers) => [
            ...modifiers,
            {
              name: action.ingredient.modifier_name,
              options: [
                {
                  id: nextItemId(),
                  name: action.ingredient.name,
                  quantity: action.ingredient.quantity,
                  price: Math.round(action.ingredient.price * 100),
                  action: action,
                },
              ],
            },
          ],
          order
        )
      }
      break
    }
    case 'remove-item':
      order = R.over(
        R.lensPath(['carts', 'items']),
        (items) =>
          items.filter((i: OrderItem) => {
            return i.id != action.item_id
          }),
        order
      )
      break
    case 'update-quantity':
      order = R.over(
        R.lensPath(['carts', 'items', R.findIndex((i) => i.id == action.item_id, order.carts.items), 'quantity']),
        (_) => action.quantity,
        order
      )
      break
    case 'remove-customization': {
      const item = findHierItem(order.carts.items, action.item_id)
      if (item) {
        const modifier = item.modifiers.find((m) => m.name == action.modifier_name)
        if (modifier) {
          modifier.options = modifier.options.filter((o) => o.id != action.customization_id)
          if (modifier.options.length == 0) {
            item.modifiers = item.modifiers.filter((m) => m.name != action.modifier_name)
          }
        }
      }
      order = { ...order }
      break
    }
    case 'add-charge':
      order = R.over(
        R.lensPath(['carts', 'cartCharges']),
        (cartCharges: CartCharge[]) => [
          ...cartCharges,
          {
            id: nextItemId(),
            name: action.charge.name,
            amount: action.charge.amount,
            parentId: null,
            taxable: false,
            action: action,
          },
        ],
        order
      )
      break
    case 'remove-charge':
      order = R.over(
        R.lensPath(['carts', 'cartCharges']),
        (cartCharges: CartCharge[]) => cartCharges.filter(({ id }) => id != action.charge_id),
        order
      )
      break
    case 'update-charge': {
      const charge = order.carts.cartCharges.find(({ id }) => id == action.charge_id) as CartCharge
      charge.amount = action.value
      break
    }
    case 'update-delivery':
      order = R.over(R.lensPath(['totals', 'delivery']), (_) => action.value, order)
      break
    case 'update-tip':
      order = R.over(R.lensPath(['totals', 'tip']), (_) => action.value, order)
      break
    case 'update-service':
      order = R.over(R.lensPath(['totals', 'service']), (_) => action.value, order)
      break
    case 'update-order_type':
      order = R.over(R.lensPath(['carts', 'settings', 'service', 'id']), (_) => action.value, order)
      order = R.over(R.lensPath(['carts', 'settings', 'service', 'label']), (_) => action.label, order)
      break
    case 'update-order_time':
      order = R.over(R.lensPath(['carts', 'settings', 'time', 'value']), (_) => action.value, order)
      break
    case 'update-discount':
      order = R.over(R.lensPath(['totals', 'discount']), (_) => action.value, order)
      break
    case 'update-delivery_address':
      order = R.over(R.lensPath(['carts', 'settings', 'service', 'address']), (_) => action.address, order)
      break
  }
  console.log('Apply action', action.op, action, order)
  return order
}

const findHierItem = (items: OrderItem[], id: number): OrderItem | null => {
  for (let i = 0; i < items.length; i++) {
    if (items[i].id == id) return items[i]
    if (items[i].adjustments) {
      const citem = findHierItem(items[i].adjustments as OrderItem[], id)
      if (citem) return citem
    }
  }
  return null
}

const findHierItemLastVersion = (item: OrderItem): OrderItem => {
  if (item.adjustments && item.adjustments.length > 0) {
    for (let i = 0; i < item.adjustments.length; i++) {
      if (item.adjustments[i].price < 0) continue
      return findHierItemLastVersion(item.adjustments[i])
    }
    return item // If we just had a delete then lets return initial item
  } else {
    return item
  }
}

export function EditOrderSvc(props: PropsWithChildren<{ order: Order; paymentOrder: PaymentOrder }>): JSX.Element {
  const { order } = props

  const restaurant = useRestaurantFromOrder(order)
  const channel = useChannelFromOrder(order)
  const menus = useMenuFromOrder(order)
  const [updatedOrder, setUpdatedOrder] = useState(props.order)

  const [actions, setActions] = useState<EditOrderAction[]>([])
  const [lastActionPosition, setLastActionPosition] = useState(0)

  useEffect(() => {
    _nextItemId = -1 // IMP: This is required in order that each reduce to produce the same ids no matter how many actions are there
    setUpdatedOrder(
      (lastActionPosition < actions.length ? actions.slice(0, lastActionPosition) : actions).reduce(
        updateOrder,
        props.order
      )
    )
  }, [actions, lastActionPosition])

  console.log('Updated order', updatedOrder)

  if (!restaurant || !channel || !menus) {
    return <>Loading restaurant and channel information...</>
  }

  const token = oauthClient.getAuthResponse() ? oauthClient.getAuthResponse().access_token : null

  // const getHierItem = (id: number, i: OrderItem): boolean =>
  //   i.id == id || (i.adjustments && i.adjustments.find((x) => getHierItem(id, x))

  const api: EditOrderAPI = {
    getRestaurantId: () => order.carts.restaurant_id,
    getMenus: (): Menus => menus,
    getItem: (cartItemId): OrderItem => findHierItem(updatedOrder.carts.items, cartItemId) as OrderItem,
    getLastItemVersion: findHierItemLastVersion,
    getTip: () => updatedOrder.totals.tip,
    getDeliveryFee: () => updatedOrder.totals.delivery,
    getService: () => updatedOrder.totals.service,
    getServiceCharge: (chargeId: number): CartCharge =>
      order.carts.cartCharges.find(({ id }) => id === chargeId) as CartCharge,
    getChanges: (): EditOrderAction[] => actions.slice(0, lastActionPosition),
    getChangesCount: (): number => lastActionPosition,
    recordChange: (action: EditOrderAction): void => {
      setActions([...(lastActionPosition < actions.length ? actions.slice(0, lastActionPosition) : actions), action])
      setLastActionPosition(lastActionPosition + 1)
    },
    discardAction: (action: EditOrderAction) => {
      setActions(actions.filter((a) => a != action))
      setLastActionPosition(lastActionPosition - 1)
    },
    undo: (): EditOrderAction | null => {
      if (lastActionPosition > 0) {
        setLastActionPosition(lastActionPosition - 1)
        return actions[lastActionPosition]
      } else {
        return null
      }
    },
    redo: (): EditOrderAction | null => {
      if (lastActionPosition < actions.length) {
        setLastActionPosition(lastActionPosition + 1)
        return actions[lastActionPosition]
      } else {
        return null
      }
    },
    hasRedo: () => lastActionPosition < actions.length,
    hasUndo: () => actions.length > 0,
    getOrderUUID: () => order.uuid,
    getCartCharges: () => updatedOrder.carts.cartCharges.filter((c) => c.type != 'convenience_fee'),
    getCartItems: () => updatedOrder.carts.items,
    commitToServer: async (paymentId: number, reason: string): Promise<unknown> => {
      const payload = {
        payment_id: paymentId,
        order_id: order.uuid,
        operations: reduceActions(
          actions.map((a: EditOrderAction) => ({ ...a, description: getActionDescription(a, restaurant.locale) }))
        ),
        comments: reason,
      }
      const res = await fetch(`${ORDERS_SVC_API5}/v5/orders/${order.uuid}/edit`, {
        method: 'POST',
        headers: authHeaders(token),
        body: JSON.stringify(payload),
      })
      if (res.ok) return res.json()
      if (res.status < 500) return res.json().then((data) => Promise.reject(data))
      return await Promise.reject({ message: `Failed to process order edit: ${res.status}` })
    },
    getLocale: () => restaurant.locale,
    getOrderType: () =>
      ({ id: updatedOrder.carts.settings.service.id, label: order.carts.settings.service.label } as ServiceInfo),
    getOrderTime: () => updatedOrder.carts.settings.time.value,
    getAvailableServices: () => restaurant.services.map(R.pick(['id', 'label'])) as ServiceInfo[],
    getDiscount: () => updatedOrder.totals.discount,
    getOrderAddress: () => updatedOrder.carts.settings.service.address,
  }

  return (
    <UserConfigAPI>
      <EditOrderCtx.Provider value={api}>
        <RestaurantCtx.Provider value={restaurant}>
          <OrderCtx.Provider value={updatedOrder} key={updatedOrder}>
            {props.children}
          </OrderCtx.Provider>
        </RestaurantCtx.Provider>
      </EditOrderCtx.Provider>
    </UserConfigAPI>
  )
}

export function getActionDescription(a: EditOrderAction, locale: string): string {
  switch (a.op) {
    case 'add-item':
      // return `Add item ${a.item.item_name} for ${utils.currency(locale, Math.round(a.item.price * 100))}`
      return `Add item ${a.item.item_name}`
    case 'add-ingredient':
      return `Add modifier ${a.ingredient.name} to ${a.item_name}`
    case 'remove-item':
      return `Remove item ${a.item_name}`
    case 'update-quantity':
      return `Update ${a.item_name} quantity from ${a.prev_quantity} to ${a.quantity}`
    case 'remove-customization':
      return `Remove modifier ${a.modifier_name}/${a.customization_name} on ${a.item_name}`
    case 'add-charge':
      return `Add charge ${a.charge.name} of ${utils.currency(locale, Math.round(a.charge.amount * 100))}`
    case 'remove-charge':
      return `Remove charge ${a.charge_name}`
    case 'update-charge':
      return `Update charge ${a.charge.name || '???'} from ${utils.currency(
        locale,
        Math.round((a.prev_value || 0) * 100)
      )} to ${utils.currency(locale, Math.round((a.charge.amount || 0) * 100))}`
    case 'update-delivery':
      return `Update delivery fee from ${utils.currency(locale, a.prev_value)} to ${utils.currency(locale, a.value)}`
    case 'update-tip':
      return `Update tip from ${utils.currency(locale, a.prev_value)} to ${utils.currency(locale, a.value)}`
    case 'update-service':
      return `Update service fee from ${utils.currency(locale, a.prev_value)} to ${utils.currency(locale, a.value)}`
    case 'update-delivery_address':
      return `Update delivery address to ${a.address.street}, ${a.address.city} (${a.address.lat},${a.address.lng})`
    case 'update-order_time':
      return `Update order time to ${a.label} from ${a.prev_label}`
    case 'update-order_type':
      return `Update order type to ${a.label} from ${a.prev_label}`
    case 'update-discount':
      return `Update discount form ${utils.currency(locale, a.prev_value)} to ${utils.currency(locale, a.value)}`
  }
}

const authHeaders = (token: string): HeadersInit => {
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  }
}

function UserConfigAPI(props: PropsWithChildren<{}>): JSX.Element {
  const userConfigProxy = {
    getConfig: (s: string) => userSettingsAPI.settingFor(s),
    setConfig: (s: string, v: string | boolean | number) => userSettingsAPI.setSettingFor(s, v),
  }
  return <UserConfigCtx.Provider value={userConfigProxy}>{props.children}</UserConfigCtx.Provider>
}

export function useRestaurantFromOrder(order: Order): Restaurant | null {
  const restaurantClient = (useContext(RestaurantsAPI5ClientCtxx) as unknown) as ApolloClient<unknown>
  const restaurantQuery = useQuery(GET_RESTAURANT_NOWF, {
    variables: {
      restaurant_id: order.carts.restaurant_id,
    },
    client: restaurantClient,
  })
  return restaurantQuery.data ? restaurantQuery.data.restaurant : null
}

export function useChannelFromOrder(order: Order): Channel | null {
  const restaurantClient = (useContext(RestaurantsAPI5ClientCtxx) as unknown) as ApolloClient<unknown>
  const channelQuery = useQuery(GET_CHANNEL, {
    variables: {
      channel_id: order.carts.channel_id, //order.carts.channel_id,
      integration_id: order.carts.integration_id,
      settingsType: 'web5',
    },
    client: restaurantClient,
  })
  return channelQuery.data ? channelQuery.data.channel : null
}

export function useMenuFromOrder(order: Order): Menus | null {
  const restaurantClient = (useContext(RestaurantsAPI5ClientCtxx) as unknown) as ApolloClient<unknown>
  const menuQuery = useQuery(GET_RESTAURANT_MENU, {
    variables: {
      restaurant_id: order.carts.restaurant_id,
      channel_id: order.carts.channel_id, //order.carts.channel_id,
      integration_id: order.carts.integration_id,
    },
    client: restaurantClient,
  })
  return menuQuery.data ? menuQuery.data.menus : null
}
